import { api } from "./api";
import type { AdminImportType } from "./types";

const UNSAFE_BROWSER_HEADERS = new Set(["content-length", "host"]);

export const ADMIN_TABULAR_IMPORT_ACCEPT = [
  ".csv",
  ".xls",
  ".xlsx",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
].join(",");

function browserSafeHeaders(headers: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(headers).filter(([name]) => !UNSAFE_BROWSER_HEADERS.has(name.toLowerCase()))
  );
}

function tabularFileMimeType(file: File) {
  const extension = file.name.toLowerCase().split(".").pop();
  if (extension === "xlsx") {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  if (extension === "xls") {
    return "application/vnd.ms-excel";
  }
  return extension === "csv" ? "text/csv" : file.type || "application/octet-stream";
}

export async function uploadAdminMasterData(
  organizationId: string,
  importType: AdminImportType,
  file: File
) {
  const initiated = await api.initiateAdminImportUpload({
    organization_id: organizationId,
    import_type: importType,
    original_file_name: file.name,
    mime_type: tabularFileMimeType(file),
    file_size_bytes: file.size
  });
  const uploadResponse = await fetch(initiated.upload.upload_url, {
    method: initiated.upload.method,
    headers: browserSafeHeaders(initiated.upload.headers),
    body: file
  });
  if (!uploadResponse.ok) {
    throw new Error(`Upload failed with HTTP ${uploadResponse.status}.`);
  }
  await api.completeAdminImportUpload(initiated.file_upload_id, organizationId);
  const result = importType === "STAFF"
    ? await api.createAdminStaffImport({
        organization_id: organizationId,
        file_upload_id: initiated.file_upload_id
      })
    : await api.createAdminImport({
        organization_id: organizationId,
        import_type: importType,
        file_upload_id: initiated.file_upload_id
      });
  if (result.status === "FAILED" || result.error_rows > 0) {
    throw new Error(result.status_message || `${file.name} could not be imported.`);
  }
  return result;
}
